import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Button, Card, Input } from '@/components';
import { JournalTagPicker } from '@/components/JournalTagPicker';
import { apiJson } from '@/lib/api';
import { formatDateLocal } from '@/lib/utils';
import type { JournalEntryListItem } from '@/lib/types';

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE = 50 * 1024 * 1024;

type PresignedUrlResponse = {
  presigned_url: string;
};

type CallType = 'FACETIME' | 'REGULAR' | 'VIDEO';
type CallDirection = 'INBOUND' | 'OUTBOUND';
type ExternalChannel = 'SMS' | 'IMESSAGE' | 'EMAIL' | 'IN_PERSON' | 'OTHER';
type EntryType = 'GENERAL' | 'PHONE_CALL' | 'EXTERNAL_MESSAGE';

function resolveEntryType(param: unknown): EntryType {
  if (param === 'PHONE_CALL') return 'PHONE_CALL';
  if (param === 'EXTERNAL_MESSAGE') return 'EXTERNAL_MESSAGE';
  return 'GENERAL';
}

function entryTypeLabel(entryType: EntryType): string {
  if (entryType === 'PHONE_CALL') return 'Phone call';
  if (entryType === 'EXTERNAL_MESSAGE') return 'External message';
  return 'General note';
}

function combineDateAndTime(dayStr: string, timeStr: string): Date {
  const [y, m, d] = dayStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  const out = new Date(y, m - 1, d, hh, mm, 0, 0);
  return out;
}

function toIsoDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function JournalRecordPage() {
  const { entryType: entryTypeParam } = Route.useSearch();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const entryType = resolveEntryType(entryTypeParam);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [entryDate, setEntryDate] = useState(() => formatDateLocal(new Date()));
  const [callType, setCallType] = useState<CallType>('FACETIME');
  const [callDirection, setCallDirection] = useState<CallDirection>('INBOUND');
  const [callStartTime, setCallStartTime] = useState('09:00');
  const [callEndTime, setCallEndTime] = useState('09:30');
  const [externalChannel, setExternalChannel] = useState<ExternalChannel>('SMS');
  const [externalDirection, setExternalDirection] = useState<CallDirection>('INBOUND');
  const [externalOccurredTime, setExternalOccurredTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [error, setError] = useState('');

  const startDt = useMemo(
    () => combineDateAndTime(entryDate, callStartTime),
    [entryDate, callStartTime]
  );
  const endDt = useMemo(
    () => combineDateAndTime(entryDate, callEndTime),
    [entryDate, callEndTime]
  );
  const externalOccurredDt = useMemo(
    () => combineDateAndTime(entryDate, externalOccurredTime),
    [entryDate, externalOccurredTime]
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      const base = {
        entry_type: entryType,
        title: title.trim() || null,
        body: body.trim() || null,
        entry_date: entryDate,
      };

      let payload: Record<string, unknown>;
      if (entryType === 'PHONE_CALL') {
        let end = endDt;
        if (end.getTime() <= startDt.getTime()) {
          end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }
        payload = {
          ...base,
          call_type: callType,
          call_direction: callDirection,
          call_start_time: startDt.toISOString(),
          call_end_time: end.toISOString(),
          tag_ids: selectedTagIds,
        };
      } else if (entryType === 'EXTERNAL_MESSAGE') {
        payload = {
          ...base,
          external_channel: externalChannel,
          external_direction: externalDirection,
          external_occurred_at: externalOccurredDt.toISOString(),
          tag_ids: selectedTagIds,
        };
      } else {
        payload = { ...base, tag_ids: selectedTagIds };
      }

      const created = await apiJson<JournalEntryListItem>(getToken, '/v1/journal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      for (const file of selectedFiles) {
        const uploadMeta = await apiJson<PresignedUrlResponse>(
          getToken,
          `/v1/journal/${created.id}/attachments/upload-url`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: file.name,
              file_type: file.type || 'application/octet-stream',
              file_size: file.size,
            }),
          }
        );

        const putRes = await fetch(uploadMeta.presigned_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed: ${file.name}`);
        }
      }

      return { created, attachmentCount: selectedFiles.length };
    },
    onSuccess: ({ attachmentCount }) => {
      track('Journal Entry Created', {
        entry_type: entryType,
        has_attachments: attachmentCount > 0,
        attachment_count: attachmentCount,
      });
      void queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      navigate({ to: '/app/journal' });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Could not save journal entry.';
      setError(msg);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const next: File[] = [];
    for (const f of files) {
      if (f.size > MAX_FILE_SIZE) {
        setError('Each file must be under 50MB.');
        continue;
      }
      next.push(f);
    }
    const merged = [...selectedFiles, ...next].slice(0, MAX_PHOTOS);
    setSelectedFiles(merged);
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const submit = () => {
    setError('');
    const hasContent =
      title.trim().length > 0 ||
      body.trim().length > 0 ||
      selectedFiles.length > 0 ||
      entryType === 'PHONE_CALL' ||
      entryType === 'EXTERNAL_MESSAGE';
    if (!hasContent) {
      setError('Enter a title, notes, pick a phone call time, or attach a photo.');
      return;
    }
    if (entryType === 'PHONE_CALL') {
      let end = endDt;
      if (end.getTime() <= startDt.getTime()) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      if (end.getTime() <= startDt.getTime()) {
        setError('End time must be after start time.');
        return;
      }
    }
    createMutation.mutate();
  };

  const chipClass = (active: boolean) =>
    `px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${
      active
        ? 'bg-primary text-white border-primary'
        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
    }`;

  return (
    <div className='min-h-screen bg-sky-light'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-6 py-4'>
          <Link
            to='/app/journal'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
            style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      <div className='max-w-2xl mx-auto px-6 py-6'>
        <Card className='space-y-4'>
          <h1
            className='text-lg font-bold text-gray-900'
            style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
          >
            {entryTypeLabel(entryType)}
          </h1>
          {entryType === 'EXTERNAL_MESSAGE' ? (
            <p className='text-sm text-gray-500'>
              Log off-platform communication (private to you)
            </p>
          ) : null}

          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Entry date</label>
            <input
              type='date'
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              max={toIsoDate(new Date())}
              disabled={createMutation.isPending}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            />
          </div>

          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Title (optional)</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='Short label'
              disabled={createMutation.isPending}
            />
          </div>

          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Tags (optional)</label>
            <JournalTagPicker
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
            />
          </div>

          {entryType === 'PHONE_CALL' ? (
            <>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Call type</label>
                <div className='flex flex-wrap gap-2'>
                  {(
                    [
                      ['FACETIME', 'FaceTime'],
                      ['REGULAR', 'Phone'],
                      ['VIDEO', 'Video'],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type='button'
                      className={chipClass(callType === v)}
                      onClick={() => setCallType(v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Direction</label>
                <div className='flex flex-wrap gap-2'>
                  {(
                    [
                      ['INBOUND', 'Inbound'],
                      ['OUTBOUND', 'Outbound'],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type='button'
                      className={chipClass(callDirection === v)}
                      onClick={() => setCallDirection(v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Start time</label>
                <input
                  type='time'
                  value={callStartTime}
                  onChange={(e) => setCallStartTime(e.target.value)}
                  disabled={createMutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>End time</label>
                <input
                  type='time'
                  value={callEndTime}
                  onChange={(e) => setCallEndTime(e.target.value)}
                  disabled={createMutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
              </div>
            </>
          ) : null}

          {entryType === 'EXTERNAL_MESSAGE' ? (
            <>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Channel</label>
                <div className='flex flex-wrap gap-2'>
                  {(
                    [
                      ['SMS', 'SMS'],
                      ['IMESSAGE', 'iMessage'],
                      ['EMAIL', 'Email'],
                      ['IN_PERSON', 'In person'],
                      ['OTHER', 'Other'],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type='button'
                      className={chipClass(externalChannel === v)}
                      onClick={() => setExternalChannel(v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Direction</label>
                <div className='flex flex-wrap gap-2'>
                  {(
                    [
                      ['INBOUND', 'Received'],
                      ['OUTBOUND', 'Sent'],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type='button'
                      className={chipClass(externalDirection === v)}
                      onClick={() => setExternalDirection(v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>Occurred at</label>
                <input
                  type='time'
                  value={externalOccurredTime}
                  onChange={(e) => setExternalOccurredTime(e.target.value)}
                  disabled={createMutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
              </div>
            </>
          ) : null}

          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Notes</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='What happened, what was said, anything important…'
              rows={5}
              disabled={createMutation.isPending}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-y min-h-[120px]'
              style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
            />
          </div>

          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Photos (optional)</label>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*'
              multiple
              className='hidden'
              onChange={handleFileSelect}
            />
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              disabled={createMutation.isPending || selectedFiles.length >= MAX_PHOTOS}
              className='text-primary font-semibold text-sm hover:underline'
            >
              Add photos ({selectedFiles.length}/{MAX_PHOTOS})
            </button>
            <ul className='mt-2 space-y-2'>
              {selectedFiles.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className='flex items-center justify-between gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2'
                >
                  <span className='text-sm text-gray-700 truncate'>{f.name}</span>
                  <button
                    type='button'
                    className='text-gray-500 text-sm font-semibold shrink-0'
                    onClick={() => removeFile(i)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {error ? <p className='text-sm text-red-600'>{error}</p> : null}

          <Button
            type='button'
            onClick={submit}
            disabled={createMutation.isPending}
            loading={createMutation.isPending}
            className='w-full'
          >
            Save entry
          </Button>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/journal/record')({
  component: JournalRecordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    entryType: resolveEntryType(search.entryType),
  }),
});
